import type { IsoDate } from '../../../../core/primitives/schema';
import type { DependentRelation, FundPolicy } from '../../../../core/fundPolicy/schema';

/**
 * Evaluates a dependent against the *stored* fund policy.
 *
 * Nothing here hardcodes an age, a relationship, or a count. Every threshold is
 * read from the policy the committee edits, which is the whole reason those
 * settings exist.
 *
 * Pure domain logic: no platform access, no I/O (§3.1). This is also enforced
 * server-side — a client-side "eligible" is a convenience, not permission.
 */

export type EligibilityFailureCode =
  | 'relation_not_allowed'
  | 'exceeds_age_limit'
  | 'exceeds_relation_count'
  | 'exceeds_total_count'
  | 'no_rule_defined';

export interface EligibilityFailure {
  readonly code: EligibilityFailureCode;
  readonly messageAr: string;
}

export interface EligibilityResult {
  readonly eligible: boolean;
  readonly failures: readonly EligibilityFailure[];
  /** True when the policy wants proof of relationship for this relation. */
  readonly requiresProofDocument: boolean;
}

export interface DependentCandidate {
  readonly relation: DependentRelation;
  readonly birthDate: IsoDate;
}

/**
 * Completed years between two dates.
 *
 * Month/day comparison rather than dividing elapsed milliseconds: a
 * 365.25-day approximation puts a birthday on the wrong side of the boundary
 * for someone turning exactly the limit age, and "covered or not" must not
 * depend on leap-year arithmetic.
 */
export function ageInYearsOn(birthDate: IsoDate, onDate: IsoDate): number {
  const [birthYear = 0, birthMonth = 0, birthDay = 0] = birthDate.split('-').map(Number);
  const [onYear = 0, onMonth = 0, onDay = 0] = onDate.split('-').map(Number);

  let age = onYear - birthYear;
  if (onMonth < birthMonth || (onMonth === birthMonth && onDay < birthDay)) {
    age -= 1;
  }
  return age;
}

export function evaluateDependentEligibility(
  candidate: DependentCandidate,
  policy: FundPolicy,
  context: {
    /** Existing active dependents of the same employee, excluding the candidate. */
    readonly existingByRelation: Readonly<Partial<Record<DependentRelation, number>>>;
    readonly existingTotal: number;
    /** Evaluation date. Passed in so results are reproducible in tests. */
    readonly asOf: IsoDate;
  },
): EligibilityResult {
  const rule = policy.dependentRules.find((r) => r.relation === candidate.relation);
  const failures: EligibilityFailure[] = [];

  if (!rule) {
    return {
      eligible: false,
      requiresProofDocument: false,
      failures: [
        {
          code: 'no_rule_defined',
          messageAr: 'لا توجد قاعدة معرّفة لهذه الصلة في سياسة الصندوق',
        },
      ],
    };
  }

  if (!rule.allowed) {
    failures.push({
      code: 'relation_not_allowed',
      messageAr: 'هذه الصلة غير مشمولة بالتغطية حاليًا',
    });
  }

  if (rule.maxAgeYears !== undefined) {
    const age = ageInYearsOn(candidate.birthDate, context.asOf);
    // Inclusive: a policy limit of 20 covers someone who is exactly 20.
    if (age > rule.maxAgeYears) {
      failures.push({
        code: 'exceeds_age_limit',
        messageAr: `تجاوز الحد العمري المسموح (${String(rule.maxAgeYears)} سنة)`,
      });
    }
  }

  if (rule.maxCount !== undefined) {
    const existing = context.existingByRelation[candidate.relation] ?? 0;
    if (existing + 1 > rule.maxCount) {
      failures.push({
        code: 'exceeds_relation_count',
        messageAr: `تجاوز الحد الأقصى لهذه الصلة (${String(rule.maxCount)})`,
      });
    }
  }

  if (context.existingTotal + 1 > policy.maxDependentsPerEmployee) {
    failures.push({
      code: 'exceeds_total_count',
      messageAr: `تجاوز الحد الأقصى لعدد المعالين (${String(policy.maxDependentsPerEmployee)})`,
    });
  }

  return {
    eligible: failures.length === 0,
    failures,
    requiresProofDocument: rule.requiresProofDocument,
  };
}
